import { css } from '@emotion/react'
import type { LearningContentStatus } from '@mathflat/domain/@entities/StudentWorksheet/api2'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import { type ReactNode, useEffect } from 'react'
import { generatePath } from 'react-router'
import { useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import { useRepository } from '~/@common/hooks/useRepository'
import modalService from '~/@common/services/modal.service'
import { textEllipsis, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { learningContentStatusText } from '~/@common/types'
import { Icon } from '~/@common/ui/Icon/Icon'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import { LearningUnavailableAlertModal } from '~/@pages/@widgets/LearningUnavailableAlertModal'
import { ChallengeStore } from '~/@pages/student/@common/store/Challenge.store'
import { LearningProcessService } from '~/@pages/student/learning-process/@widgets/service/LearningProcess.service'

import type { AssignedChallenge } from '../../@common/response'
import AssignedLearningService from '../../@service/AssignedLearning.service'

const WORKSHEET_STATUS = {
  INCOMPLETE: '학습가능',
  PROGRESS: '풀이중',
  COMPLETE: '학습완료',
}

const WORKSHEET_TYPE = {
  WORKSHEET: '학습지',
  WORKBOOK: '교재',
  EXAM: '경시대회',
  HOMEWORK: '숙제',
}

const AssignedLearningSection = observer(() => {
  const service = useRepository(AssignedLearningService)
  const learningProcessService = useRepository(LearningProcessService)
  const challengeStore = useRepository(ChallengeStore)
  const navigate = useNavigate()

  useEffect(() => {
    service.fetchAllAssignedLearning()
  }, [service])

  const startChallengeLearning = async (status: AssignedChallenge['status']) => {
    const challenge = service.assignedChallenge
    if (!challenge) return

    const { conceptChipId, littleChapterId } = challenge

    try {
      const 챌린지학습 = await learningProcessService.챌린지학습_시작하기({
        conceptChipIds: [conceptChipId],
        littleChapterId,
        referenceId: conceptChipId,
        referenceType: 'CONCEPT_CHIP',
      })

      if (챌린지학습.이어하기_여부) {
        const url = await 챌린지학습.이어하기()
        challengeStore.clearSelectedChallenge()
        navigate(url)
      } else {
        const url = await 챌린지학습.새로하기()
        challengeStore.clearSelectedChallenge()
        navigate(url)
      }

      // 학습지가 없어서 챌린지 단일 버튼이 나올 경우는 true, 둘다 나오는 경우는 false
      const isSingleButton = service.assignedWorksheet ? false : true

      if (status === 'INCOMPLETE') {
        mixpanelClient.track('챌린지학습_시작하기_Click', {
          소단원정보: challenge.littleChapterName,
          학년정보: service.assignedChallengeInfoString ?? '',
          버튼타입: isSingleButton,
        })
      }

      if (status === 'PROGRESS') {
        mixpanelClient.track('챌린지학습_이어서학습하기_Click', {
          소단원정보: challenge.littleChapterName,
          학년정보: service.assignedChallengeInfoString ?? '',
          버튼타입: isSingleButton,
        })
      }
    } catch {
      modalService.openModal(<LearningUnavailableAlertModal />, {
        modalName: '학습 불가',
      })
    }
  }

  const handleClickAssignedWorksheet = (status: Exclude<LearningContentStatus, 'EXPIRED'>) => {
    const worksheet = service.assignedWorksheet
    if (!worksheet) return

    if (status === 'COMPLETE') {
      navigate(
        generatePath(routeName.student.studentWorksheetScoringById, {
          studentWorksheetId: String(worksheet.studentWorksheetId ?? worksheet.id),
        }),
      )
    } else {
      navigate(
        generatePath(routeName.student.studentWorksheetById, {
          studentWorksheetId: String(worksheet.studentWorksheetId ?? worksheet.id),
        }),
      )

      // 챌린지가 없어서 학습지 단일 버튼이 나올 경우는 true, 둘다 나오는 경우는 false
      const isSingleButton = service.assignedChallenge ? false : true

      if (status === 'INCOMPLETE') {
        mixpanelClient.track('학습지풀기_시작하기_Click', {
          학습지명: worksheet.title,
          학습지타입: WORKSHEET_TYPE[worksheet.type],
          학습지상태: WORKSHEET_STATUS[worksheet.status],
          버튼타입: isSingleButton,
        })
      }

      if (status === 'PROGRESS') {
        mixpanelClient.track('학습지풀기_이어서학습하기_Click', {
          학습지명: worksheet.title,
          학습지타입: WORKSHEET_TYPE[worksheet.type],
          학습지상태: WORKSHEET_STATUS[worksheet.status],
          버튼타입: isSingleButton,
        })
      }
    }
  }

  if (service.isEmptyAssignedLearning || service.isLoading) {
    return <EmptySection />
  }

  return (
    <div css={S.AssignedCards}>
      {service.assignedChallenge && (
        <AssignedCard
          variant="challenge"
          subTitle={`챌린지 학습 | ${service.assignedChallengeInfoString}`}
          title={`${service.assignedChallenge.littleChapterName}`}
          description={`개념 ${service.assignedChallengeConceptChipCount.개념}개 ㅣ 기본 ${service.assignedChallengeConceptChipCount.기본}개 ㅣ 심화 ${service.assignedChallengeConceptChipCount.심화}개`}
          status={service.assignedChallenge.status}
          onStart={startChallengeLearning}
        />
      )}

      {service.assignedWorksheet && service.assignedWorksheet.status !== 'EXPIRED' && (
        <AssignedCard
          variant="worksheet"
          subTitle={`학습지 학습 | ${format(new Date(service.assignedWorksheet.datetime), 'MM월dd일')}`}
          title={`${service.assignedWorksheet.title}`}
          description={`${service.assignedWorksheet.problemsCount}문제 | ${learningContentStatusText[service.assignedWorksheet.status]}`}
          status={service.assignedWorksheet.status}
          onStart={handleClickAssignedWorksheet}
        />
      )}
    </div>
  )
})

export default AssignedLearningSection

type AssignedCardProps = {
  variant: 'challenge' | 'worksheet'
  subTitle: ReactNode
  title: string
  description: string
  status: Exclude<LearningContentStatus, 'EXPIRED'> | AssignedChallenge['status']
  onStart: (status: Exclude<LearningContentStatus, 'EXPIRED'> | AssignedChallenge['status']) => void
}

const AssignedCard = ({
  variant,
  subTitle,
  title,
  description,
  status,
  onStart,
}: AssignedCardProps) => {
  return (
    <div css={[S.AssignedCard, S[variant]]}>
      <div>
        <div className="sub-title">{subTitle}</div>
        <div className="title">{title}</div>
        <div className="description">{description}</div>
      </div>
      {status === 'INCOMPLETE' && (
        <button onClick={() => onStart(status)}>
          시작하기
          <Icon name="icon_chevron_right" color="var(--White)" size={20} />
        </button>
      )}
      {status === 'PROGRESS' && (
        <button onClick={() => onStart(status)}>
          <span className="progress-text-desktop">이어서 학습하기</span>
          <span className="progress-text-under-desktop">이어하기</span>
          <Icon name="icon_chevron_right" color="var(--White)" size={20} />
        </button>
      )}
      {status === 'COMPLETE' &&
        (variant === 'challenge' ? (
          <button disabled>학습 완료</button>
        ) : (
          <button onClick={() => onStart(status)}>
            <span className="learning-text">학습 </span>결과보기
          </button>
        ))}
      <img
        src={s3URL.student(
          variant === 'challenge' ? 'images/challenge_smile_red.svg' : 'images/worksheet_blue.svg',
        )}
        alt="assigned_learning_image"
      />
    </div>
  )
}

const EmptySection = () => {
  const EMPTY_CONTENTS = [
    {
      type: 'challenge',
      description: '아직 챌린지가 설정되지 않았어요.\n선생님께 요청해요!',
    },
    {
      type: 'worksheet',
      description: `아직 학습지가 ${tJosa('출제')}되지 않았어요.\n선생님께 요청해요!`,
    },
  ] as const

  return (
    <div css={S.Empty}>
      {EMPTY_CONTENTS.map(({ type, description }) => (
        <div key={type} className="empty-content">
          <div className="empty-content-description">{description}</div>

          <button>
            시작하기
            <Icon name="icon_chevron_right" color="var(--Colors-Ash-500)" size={20} />
          </button>
          <img
            src={s3URL.student(
              type === 'challenge'
                ? 'images/challenge_smile_white.svg'
                : 'images/worksheet_white.svg',
            )}
            alt="empty_assigned_learning_image"
          />
        </div>
      ))}
    </div>
  )
}

const S = {
  AssignedCards: css`
    display: flex;
    gap: 10px;
  `,

  AssignedCard: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 24px;
    height: 300px;
    border-radius: var(--Radius-300);
    position: relative;
    > *:not(img) {
      position: relative;
      z-index: 1;
    }

    .sub-title {
      ${typo.heading05};
      font-weight: 700;
      margin-bottom: 14px;
    }

    .learning-text {
      ${mediaQuery.underDesktop} {
        display: none;
      }
    }
    .progress-text-desktop {
      ${mediaQuery.underDesktop} {
        display: none;
      }
    }
    .progress-text-under-desktop {
      display: none;
      ${mediaQuery.underDesktop} {
        display: inline;
      }
    }

    .title {
      ${typo.heading02};
      font-weight: 700;
      color: var(--White);
      margin-bottom: 4px;
      ${textEllipsis(2)}

      ${mediaQuery.underDesktop} {
        ${typo.heading03};
        ${textEllipsis(2)}
      }
    }

    .description {
      ${typo.heading05};
      color: var(--White);

      ${mediaQuery.underDesktop} {
        ${typo.body02};
      }
    }

    button {
      ${typo.heading05};
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--Radius-300);
      background: var(--Colors-Gray-900);
      color: var(--White);
      padding: 14px 16px;
      font-weight: 700;
      gap: 4px;

      &:disabled {
        background: var(--Colors-Gray-100);
        color: var(--Colors-Gray-500);
        cursor: not-allowed;
      }

      ${mediaQuery.underDesktop} {
        svg {
          display: none;
        }
      }
    }

    img {
      position: absolute;
      right: 24px;
      bottom: 56px;
      width: clamp(115px, 50%, 220px);
      height: auto;
      z-index: 0;
      pointer-events: none;
    }
  `,

  challenge: css`
    background-color: var(--Colors-Red-400);

    .sub-title {
      color: var(--Colors-Red-800);
    }
  `,

  worksheet: css`
    background-color: var(--Colors-Sky-400);

    .sub-title {
      color: var(--Colors-Sky-800);
    }
  `,

  Empty: css`
    display: flex;
    height: 300px;
    gap: 10px;
    flex: 1;
    word-break: keep-all;

    .empty-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 24px;
      border-radius: var(--Radius-300);
      border: 2px dashed var(--Colors-Ash-400);
      background: var(--Colors-Ash-200);
      position: relative;
      width: 100%;

      > *:not(img) {
        z-index: 1;
      }
    }

    .empty-content-description {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 200px;
      ${typo.heading05};
      color: var(--Colors-Ash-600);
      text-align: center;
      font-weight: 700;
      white-space: pre-line;
    }

    button {
      ${typo.heading05};
      color: var(--Colors-Ash-500);
      font-weight: 700;
      width: 100%;
      padding: 14px;
      border-radius: var(--Radius-300);
      background: var(--Colors-Ash-300);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      z-index: 1;
    }

    img {
      position: absolute;
      right: 0;
      bottom: 56px;
      width: clamp(115px, 50%, 220px);
      height: auto;
      z-index: 0;
      pointer-events: none;
    }
  `,
}
